import { JSX, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Badge } from "@via/components/badge.tsx";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import useUrlParams from "@via/hooks/useURLParams.tsx";
import {
  useCreateMcpServer,
  useDeleteMcpServer,
  useMcpServerById,
  useMcpServersList,
  useRefreshMcpServerTools,
  useToggleMcpServerStatus,
  useUpdateMcpServer,
} from "@via/pages/cortex/mcp-server/mcp-server.api.tsx";
import {
  BadgeStatus,
  EMPTY_TABLE_MESSAGE,
  ERROR,
} from "@via/utils/constants.tsx";
import { getElapsedTime } from "@via/utils/helpers.tsx";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import McpServerFilter from "@via/pages/cortex/mcp-server/mcp-server-filter.tsx";
import Drawer from "@via/components/drawer.tsx";
import { DrawerSize } from "@via/components/table/table.tsx";
import McpServerForm from "@via/pages/cortex/mcp-server/mcp-server-form.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import {
  CreateMcpServerRequestBody,
  McpConnectionStatus,
  McpServerDetail,
  McpServerListItem,
  UpdateMcpServerRequestBody,
} from "common";

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

const MCP_TABLE_OPTIONS = [
  { name: "Edit", href: "#" },
  { name: "Refresh Tools", href: "#" },
  { name: "Delete", href: "#" },
];

const ConnectionStatusBadge = ({
  status,
  lastError,
}: {
  status: McpConnectionStatus;
  lastError?: string | null;
}) => {
  switch (status) {
    case McpConnectionStatus.Connected:
      return (
        <Badge
          text="Connected"
          fillColor={BadgeStatus.SuccessFillColor}
          textColor={BadgeStatus.SuccessTextColor}
        />
      );
    case McpConnectionStatus.Error:
      return (
        <span
          data-tooltip-id="mcp-connection-error-tooltip"
          data-tooltip-content={lastError || "Connection error"}>
          <Badge
            text="Error"
            fillColor={BadgeStatus.ErrorFillColor}
            textColor={BadgeStatus.ErrorTextColor}
          />
        </span>
      );
    case McpConnectionStatus.Disconnected:
    default:
      return (
        <Badge
          text="Disconnected"
          fillColor={BadgeStatus.DefaultFillColor}
          textColor={BadgeStatus.DefaultTextColor}
        />
      );
  }
};

const transportTypeLabel: Record<string, string> = {
  "http-streamable": "HTTP Streamable",
  sse: "SSE",
  websocket: "WebSocket",
};

const McpServer = (): JSX.Element => {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);

  // Drawer states
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [mcpToBeEdited, setMcpToBeEdited] = useState<McpServerListItem | null>(
    null
  );
  const [mcpToBeDeleted, setMcpToBeDeleted] =
    useState<McpServerListItem | null>(null);

  // API hooks
  const {
    data: mcpsList,
    isError: isMcpsListError,
    isLoading: isMcpsListLoading,
  } = useMcpServersList(page, pageSize, sort, columnFilters);

  const { mutateAsync: createMcpServer, isPending: isCreateLoading } =
    useCreateMcpServer();
  const { mutateAsync: updateMcpServer, isPending: isUpdateLoading } =
    useUpdateMcpServer();
  const { mutateAsync: deleteMcpServer, isPending: isDeleteLoading } =
    useDeleteMcpServer();
  const {
    mutateAsync: toggleMcpServerStatus,
    isPending: isToggling,
    variables: togglingServerId,
  } = useToggleMcpServerStatus();
  const {
    mutateAsync: refreshMcpServerTools,
    isPending: isRefreshingTools,
    variables: refreshingToolsServerId,
  } = useRefreshMcpServerTools();

  // Fetch full detail for editing (includes headers and advancedConfig)
  const { data: editDetail, isLoading: isEditDetailLoading } = useMcpServerById(
    mcpToBeEdited?.id ?? null
  );

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleContextMenuAction = (
    name: string,
    mcp: McpServerListItem
  ): void => {
    switch (name) {
      case "Edit":
        setMcpToBeEdited(mcp);
        setIsEditDrawerOpen(true);
        break;
      case "Refresh Tools":
        refreshMcpServerTools({ id: mcp.id, name: mcp.name });
        break;
      case "Delete":
        setMcpToBeDeleted(mcp);
        break;
    }
  };

  const handleAddSubmit = async (
    payload: CreateMcpServerRequestBody | UpdateMcpServerRequestBody
  ): Promise<void> => {
    await createMcpServer(payload as CreateMcpServerRequestBody);
    setIsCreateDrawerOpen(false);
  };

  const handleEditSubmit = async (
    payload: CreateMcpServerRequestBody | UpdateMcpServerRequestBody
  ): Promise<void> => {
    if (!mcpToBeEdited) return;
    await updateMcpServer({
      id: mcpToBeEdited.id,
      payload: payload as UpdateMcpServerRequestBody,
    });
    setIsEditDrawerOpen(false);
    setMcpToBeEdited(null);
  };

  const handleDelete = async (): Promise<void> => {
    if (!mcpToBeDeleted) return;
    await deleteMcpServer({ id: mcpToBeDeleted.id, name: mcpToBeDeleted.name });
    setMcpToBeDeleted(null);
  };

  const handleToggleEnabled = async (mcp: McpServerListItem): Promise<void> => {
    await toggleMcpServerStatus({
      id: mcp.id,
      name: mcp.name,
      isEnabled: !mcp.isEnabled,
    });
  };

  // ── Columns ──────────────────────────────────────────────────────────────

  const columnHelper = createColumnHelper<McpServerListItem>();
  const columns = [
    columnHelper.accessor("name", {
      cell: info => <span className="font-medium">{info.getValue()}</span>,
      header: () => <span>Name</span>,
      size: 180,
    }),
    columnHelper.accessor("transportType", {
      cell: info => (
        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          {transportTypeLabel[info.getValue()] || info.getValue()}
        </span>
      ),
      header: () => <span>Transport</span>,
      size: 120,
      enableSorting: true,
    }),
    columnHelper.accessor("connectionStatus", {
      cell: info => (
        <ConnectionStatusBadge
          status={info.getValue()}
          lastError={info.row.original.lastError}
        />
      ),
      header: () => <span>Connection</span>,
      size: 110,
      enableSorting: true,
    }),
    columnHelper.accessor("toolCount", {
      cell: info => {
        const count = info.getValue();
        return (
          <span className="text-sm text-gray-600">
            {count > 0 ? (
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                {count} tool{count === 1 ? "" : "s"}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </span>
        );
      },
      header: () => <span>Tools</span>,
      size: 80,
      enableSorting: false,
    }),
    columnHelper.accessor("description", {
      cell: info => (
        <span className="block line-clamp-2 max-w-md mr-10 text-gray-600">
          {info.getValue() || "-"}
        </span>
      ),
      header: () => <span>Description</span>,
      size: 200,
      enableSorting: false,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">{getElapsedTime(value.toString())}</span>
        );
      },
      header: () => <span>Modified</span>,
      size: 110,
    }),
    columnHelper.accessor("isEnabled", {
      cell: info => {
        const serverId = info.row.original.id;
        const isBusy =
          (isToggling && togglingServerId?.id === serverId) ||
          (isRefreshingTools && refreshingToolsServerId?.id === serverId);
        return (
          <ToggleButton
            isOn={info.row.original.isEnabled}
            onToggle={() => handleToggleEnabled(info.row.original)}
            disabled={isBusy}
            isLoading={isBusy}
          />
        );
      },
      header: () => <span>Enabled</span>,
      size: 70,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: info => (
        <TableContextMenu
          options={MCP_TABLE_OPTIONS}
          onSelect={option => {
            handleContextMenuAction(option.name, info.row.original);
          }}
        />
      ),
      header: () => <span className="text-right w-full">Actions</span>,
      size: 70,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<McpServerListItem>>;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <main className="py-4">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">MCP Servers</h2>
        <ActionButton
          button="Add MCP Server"
          primary={true}
          onClick={() => {
            setIsCreateDrawerOpen(true);
          }}
        />
      </div>

      {/* Create Drawer */}
      <Drawer
        id="create-mcp-server"
        open={isCreateDrawerOpen}
        setOpen={setIsCreateDrawerOpen}
        size={DrawerSize.Medium}>
        <McpServerForm
          setOpen={setIsCreateDrawerOpen}
          onSubmit={handleAddSubmit}
          isLoading={isCreateLoading}
        />
      </Drawer>

      {/* Edit Drawer */}
      <Drawer
        id="edit-mcp-server"
        open={isEditDrawerOpen}
        setOpen={open => {
          setIsEditDrawerOpen(open);
          if (!open) setMcpToBeEdited(null);
        }}
        size={DrawerSize.Medium}>
        {isEditDetailLoading ? (
          <div className="p-4 grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-2">
            <SkeletonLoader count={12} />
          </div>
        ) : (
          <McpServerForm
            setOpen={open => {
              setIsEditDrawerOpen(open);
              if (!open) setMcpToBeEdited(null);
            }}
            onSubmit={handleEditSubmit}
            isLoading={isUpdateLoading}
            editData={editDetail as McpServerDetail | null}
          />
        )}
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal
        item={mcpToBeDeleted}
        setItem={setMcpToBeDeleted}
        deleteFunction={handleDelete}
        isLoading={isDeleteLoading}
        title="Delete MCP Server"
      />

      {isMcpsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isMcpsListLoading}
          data={mcpsList?.data ?? []}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={mcpsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.DEFAULT}
          applyColumnSize={true}
          additionalFilters={
            <McpServerFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          }
        />
      )}
      <Tooltip
        id="mcp-connection-error-tooltip"
        place="bottom"
        className="z-10 max-w-sm"
      />
    </main>
  );
};

export default McpServer;
